import React, { useState, useEffect } from "react";
import {
  collection, getDocs, orderBy, query, deleteDoc, doc,
  getDoc, updateDoc, serverTimestamp, addDoc, where,
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage } from "../firebase";


/**
 * Gehoert diese Storage-Datei uns — darf sie also geloescht werden?
 * Eigene Uploads liegen unter `drafts/` (AdminUploadPage). Alles andere,
 * insbesondere `candidates/…` des Personalization Tools, bleibt liegen.
 * Dieselbe Regel steht in functions/index.js; aendert sich eine Seite,
 * muss die andere mit.
 */
export const isOwnDraftFile = (path) =>
  typeof path === "string" && path.startsWith("drafts/");

/**
 * Ein E-Mail-Fehler an einem Entwurf in ganzen Sätzen: welcher Auftrag,
 * welche Kundin, welche E-Mail, was das für den Auftrag bedeutet, was zu tun
 * ist — und der gespeicherte Grund.
 *
 * Wunsch von Tobias (16.09.2026): Fehlermeldungen, die einen Auftrag aufhalten
 * können, müssen sich ohne Suchen einem Auftrag zuordnen lassen. Das Banner
 * sagte bis dahin nur „1 Bestätigungs-E-Mail … nicht versendet — Grund im
 * Tooltip am ⚠-Symbol“, ohne Auftragsnummer.
 * Ältere Dokumente tragen statt eines Grundes nur `true`; das wird ehrlich
 * als „nicht aufgezeichnet“ benannt.
 */
export const describeEmailError = (d) => {
  const order = d?.orderNumber ? `#${d.orderNumber}` : "(ohne Auftragsnummer)";
  const who = [d?.customerFirstName, d?.customerEmail && `(${d.customerEmail})`]
    .filter(Boolean).join(" ") || "Kundin unbekannt";
  const decided = !!d?.reviewedAt;
  const decision = d?.customerDecision === "modification"
    ? "dem Änderungswunsch" : "der Freigabe";
  const reason = typeof d?.emailSendError === "string" && d.emailSendError.trim()
    ? `Grund laut E-Mail-Dienst: ${d.emailSendError.trim()}`
    : "Der genaue Grund wurde damals nicht aufgezeichnet.";
  if (decided) {
    return {
      order, who, severity: "info",
      headline: `Bestätigungs-E-Mail nach ${decision} nicht versendet`,
      effect: "Die Kundin hat bereits entschieden – der Auftrag läuft normal weiter. " +
              "Sie hat nur keine Eingangsbestätigung per E-Mail bekommen.",
      action: "Bei Bedarf kurz von Hand bestätigen, dann „✓ Erledigt“.",
      reason,
    };
  }
  return {
    order, who, severity: "blocking",
    headline: "Entwurfs-E-Mail nicht bei der Kundin angekommen",
    effect: "Die Kundin hat den Entwurf nicht erhalten – der Auftrag wartet, bis sie ihn sieht.",
    action: "E-Mail-Adresse prüfen und in der Zeile „↺ Erneut“ klicken " +
            "(bei Etsy-Relay-Adressen „Auf Etsy posten“).",
    reason,
  };
};

const formatDate = (timestamp) => {
  if (!timestamp) return "—";
  const d = timestamp.toDate();
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const getStatus = (data) => {
  if (data.draftArchived) return "archived";
  if (!data.reviewedAt) return "pending";
  if (data.customerDecision === "modification") return "modification";
  return "approved";
};

const STATUS_STYLES = {
  pending:      { background: "#fefcbf", color: "#744210", border: "1px solid #f6e05e" },
  modification: { background: "#ffecd2", color: "#7b341e", border: "1px solid #f6ad55" },
  approved:     { background: "#c6f6d5", color: "#22543d", border: "1px solid #68d391" },
  archived:     { background: "#f7fafc", color: "#a0aec0", border: "1px solid #e2e8f0" },
};
const STATUS_LABELS = {
  pending:      "Pending",
  modification: "Modification",
  approved:     "Approved",
  archived:     "Archiviert",
};

const StatusBadge = ({ status, onClick }) => (
  <span
    onClick={onClick}
    style={{
      ...STATUS_STYLES[status],
      padding: "0.2em 0.65em",
      borderRadius: "12px",
      fontSize: "0.78rem",
      fontWeight: "600",
      whiteSpace: "nowrap",
      cursor: status === "modification" ? "pointer" : "default",
      textDecoration: status === "modification" ? "underline dotted" : "none",
    }}
    title={status === "modification" ? "Click to view modification request" : undefined}
  >
    {STATUS_LABELS[status]}
  </span>
);

// ── Modification detail modal ─────────────────────────────────────────────────

const ModificationModal = ({ draft, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "1em",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "#fff", borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        width: "100%", maxWidth: "520px",
        padding: "2em",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "1.25em" }}>
        <div>
          <h3 style={{ margin: "0 0 0.3em", fontSize: "1.1rem", color: "#1a202c" }}>
            Modification Request
          </h3>
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#718096" }}>
            {draft.customerFirstName && <span>{draft.customerFirstName} · </span>}
            {draft.customerEmail}
            {draft.orderNumber && <span> · Order #{draft.orderNumber}</span>}
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none", border: "none", fontSize: "1.4rem",
            color: "#a0aec0", cursor: "pointer", lineHeight: 1,
            padding: "0 0.2em", marginLeft: "1em",
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Received date */}
      <p style={{ margin: "0 0 1em", fontSize: "0.82rem", color: "#a0aec0" }}>
        Feedback received: {formatDate(draft.reviewedAt)}
      </p>

      {/* Feedback text */}
      <div style={{
        background: "#fdf7f4", border: "1px solid #e2e8f0",
        borderRadius: "8px", padding: "1em 1.25em",
        minHeight: "80px",
      }}>
        {draft.customerFeedback ? (
          <p style={{ margin: 0, color: "#2d3748", lineHeight: "1.7",
            whiteSpace: "pre-wrap", fontStyle: "italic" }}>
            "{draft.customerFeedback}"
          </p>
        ) : (
          <p style={{ margin: 0, color: "#a0aec0", fontStyle: "italic" }}>
            No written feedback provided.
          </p>
        )}
      </div>

      {/* Footer */}
      <div style={{ marginTop: "1.5em", textAlign: "right" }}>
        <button
          onClick={onClose}
          style={{
            padding: "0.5em 1.4em", borderRadius: "6px",
            border: "1px solid #cbd5e0", background: "#fff",
            color: "#4a5568", fontWeight: "600", fontSize: "0.875rem",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

// ── Delete confirmation modal ─────────────────────────────────────────────────

const ConfirmDeleteModal = ({ count, onConfirm, onCancel, deleting }) => (
  <div
    onClick={onCancel}
    style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1100, padding: "1em",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "#fff", borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        width: "100%", maxWidth: "420px",
        padding: "2em",
      }}
    >
      <h3 style={{ margin: "0 0 0.75em", fontSize: "1.1rem", color: "#1a202c" }}>
        Entwürfe löschen
      </h3>
      <p style={{ margin: "0 0 1.5em", color: "#4a5568", lineHeight: "1.6" }}>
        Bist du sicher? Du löscht <strong>{count} Entwurf{count !== 1 ? "e" : ""}</strong>{" "}
        sowie die dazugehörigen Bilddateien. Diese Aktion kann nicht rückgängig gemacht werden.
      </p>
      <div style={{ display: "flex", gap: "0.75em", justifyContent: "flex-end" }}>
        <button
          onClick={onCancel}
          disabled={deleting}
          style={{
            padding: "0.5em 1.4em", borderRadius: "6px",
            border: "1px solid #cbd5e0", background: "#fff",
            color: "#4a5568", fontWeight: "600", fontSize: "0.875rem",
            cursor: deleting ? "not-allowed" : "pointer",
          }}
        >
          Abbrechen
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          style={{
            padding: "0.5em 1.4em", borderRadius: "6px",
            border: "none",
            background: deleting ? "#a0aec0" : "#c53030",
            color: "#fff", fontWeight: "700", fontSize: "0.875rem",
            cursor: deleting ? "not-allowed" : "pointer",
          }}
        >
          {deleting ? "Wird gelöscht…" : `${count} löschen`}
        </button>
      </div>
    </div>
  </div>
);

const DraftErrorBadge = ({ draft, retrying, onRetry }) => (
  <>
    <span title={draft.reminderError} style={{
      marginLeft: "0.4em", padding: "0.1em 0.45em",
      borderRadius: "10px", border: "1px solid #fc8181",
      background: "#fff5f5", color: "#c53030",
      fontSize: "0.72rem", fontWeight: "600",
      whiteSpace: "nowrap", cursor: "help",
    }}>
      ⚠ E-Mail Fehler
    </span>
    <button onClick={() => onRetry(draft)} disabled={!!retrying[draft.id]} style={{
      marginLeft: "0.4em", padding: "0.1em 0.5em",
      borderRadius: "10px", border: "1px solid #90cdf4",
      background: "#ebf8ff", color: "#2b6cb0",
      fontSize: "0.72rem", fontWeight: "600", whiteSpace: "nowrap",
      cursor: retrying[draft.id] ? "default" : "pointer",
      opacity: retrying[draft.id] ? 0.6 : 1,
    }}>
      {retrying[draft.id] ? "…" : "↺ Erneut"}
    </button>
  </>
);

// ── Main component ─────────────────────────────────────────────────────────────

const DraftOverview = () => {
  const [drafts, setDrafts]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState("aktiv");
  const [search, setSearch]       = useState("");
  const [modal, setModal]         = useState(null);

  // Order stage cache for stale detection: { orderNumber → workflowStage }
  const [orderStages, setOrderStages] = useState({});

  // Delete mode state
  const [deleteMode, setDeleteMode]   = useState(false);
  const [selected, setSelected]       = useState(new Set());
  const [dateFrom, setDateFrom]       = useState("");
  const [dateTo, setDateTo]           = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [retrying, setRetrying]             = useState({});
  const [retryingInitial, setRetryingInitial] = useState({});
  const [dismissingError, setDismissingError] = useState({});
  const [problemDomains, setProblemDomains] = useState([]);
  const [etsyQueueMap,   setEtsyQueueMap]   = useState({}); // draftId → queue doc data
  const [retryingEtsy,   setRetryingEtsy]   = useState({}); // draftId → true

  const handleRetry = async (draft) => {
    setRetrying((r) => ({ ...r, [draft.id]: true }));
    try {
      const fn = httpsCallable(
        getFunctions(undefined, "europe-west1"),
        "retryDraftReminder",
      );
      await fn({ draftId: draft.id });
      const snap = await getDoc(doc(db, "drafts", draft.id));
      if (snap.exists()) {
        setDrafts((prev) =>
          prev.map((d) => (d.id === draft.id ? { id: snap.id, ...snap.data() } : d))
        );
      }
    } catch (err) {
      alert(`Fehler beim erneuten Senden: ${err.message}`);
    } finally {
      setRetrying((r) => ({ ...r, [draft.id]: false }));
    }
  };

  // Warnung ausblenden, ohne den Grund zu verlieren.
  const handleDismissEmailError = async (draft) => {
    setDismissingError((r) => ({ ...r, [draft.id]: true }));
    try {
      await updateDoc(doc(db, "drafts", draft.id), {
        emailErrorDismissedAt: serverTimestamp(),
      });
      setDrafts((prev) => prev.map((d) => (d.id === draft.id
        ? { ...d, emailErrorDismissedAt: new Date() } : d)));
    } catch (err) {
      alert(`Konnte den Hinweis nicht ausblenden: ${err.message}`);
    } finally {
      setDismissingError((r) => ({ ...r, [draft.id]: false }));
    }
  };

  const handleRetryInitial = async (draft) => {
    setRetryingInitial((r) => ({ ...r, [draft.id]: true }));
    try {
      const fn = httpsCallable(
        getFunctions(undefined, "europe-west1"),
        "retryInitialDraftEmail",
      );
      await fn({ draftId: draft.id });
      const snap = await getDoc(doc(db, "drafts", draft.id));
      if (snap.exists()) {
        setDrafts((prev) =>
          prev.map((d) =>
            d.id === draft.id ? { id: snap.id, ...snap.data() } : d
          )
        );
      }
    } catch (err) {
      alert(`Fehler beim erneuten Senden: ${err.message}`);
    } finally {
      setRetryingInitial((r) => ({ ...r, [draft.id]: false }));
    }
  };

  useEffect(() => {
    const fetchDrafts = async () => {
      try {
        const [snapshot, cfgSnap, queueSnap] = await Promise.all([
          getDocs(query(collection(db, "drafts"), orderBy("createdAt", "desc"))),
          getDoc(doc(db, "config", "syncSettings")),
          getDocs(query(
            collection(db, "etsy_message_queue"),
            where("messageType", "==", "draft_welcome"),
          )),
        ]);
        const loaded = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDrafts(loaded);
        setProblemDomains((cfgSnap.exists() ? cfgSnap.data().problemDomains : null) || []);
        // Build map of most-recent queue entry per draft
        const qmap = {};
        queueSnap.docs.forEach((d) => {
          const data = { id: d.id, ...d.data() };
          const did = data.draftId;
          if (!did) return;
          const existing = qmap[did];
          if (!existing || (data.createdAt?.toMillis?.() || 0) > (existing.createdAt?.toMillis?.() || 0)) {
            qmap[did] = data;
          }
        });
        setEtsyQueueMap(qmap);

        // For all non-archived drafts with an orderNumber, fetch the order's
        // current stage to detect stale ones (order moved out of "entwurf_raus")
        const orderNumbers = [
          ...new Set(
            loaded
              .filter((d) => d.orderNumber && !d.draftArchived)
              .map((d) => d.orderNumber)
          ),
        ];
        if (orderNumbers.length > 0) {
          const stages = {};
          await Promise.all(
            orderNumbers.map(async (num) => {
              const snap = await getDoc(doc(db, "etsy_orders", num));
              if (snap.exists()) stages[num] = snap.data().workflowStage || "";
            })
          );
          setOrderStages(stages);
        }
      } catch (err) {
        console.error("Failed to load drafts:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDrafts();
  }, []);

  const isDomainProblem = (email) => {
    if (!email || !problemDomains.length) return false;
    const domain = email.split("@")[1]?.toLowerCase() || "";
    return problemDomains.some((d) => domain === d || domain.endsWith("." + d));
  };

  const handlePostDraftToEtsy = async (draft) => {
    try {
      const newDoc = await addDoc(collection(db, "etsy_message_queue"), {
        orderId:     draft.orderNumber || "",
        draftId:     draft.id,
        messageType: "draft_welcome",
        status:      "pending",
        createdAt:   serverTimestamp(),
      });
      setEtsyQueueMap((prev) => ({
        ...prev,
        [draft.id]: { id: newDoc.id, draftId: draft.id, messageType: "draft_welcome", status: "pending" },
      }));
    } catch (err) {
      alert(`Fehler: ${err.message}`);
    }
  };

  const handleRetryDraftEtsy = async (draft) => {
    setRetryingEtsy((r) => ({ ...r, [draft.id]: true }));
    try {
      const newDoc = await addDoc(collection(db, "etsy_message_queue"), {
        orderId:     draft.orderNumber || "",
        draftId:     draft.id,
        messageType: "draft_welcome",
        status:      "pending",
        createdAt:   serverTimestamp(),
      });
      setEtsyQueueMap((prev) => ({
        ...prev,
        [draft.id]: { id: newDoc.id, draftId: draft.id, messageType: "draft_welcome", status: "pending" },
      }));
    } catch (err) {
      alert(`Fehler: ${err.message}`);
    } finally {
      setRetryingEtsy((r) => ({ ...r, [draft.id]: false }));
    }
  };

  const searchTerm = search.trim().toLowerCase();
  // Zwei ganz verschiedene Faelle teilen sich dasselbe Feld:
  //  - reviewedAt fehlt  -> die Entwurfs-E-Mail kam nicht beim Kunden an,
  //                         "Erneut" ist die richtige Reaktion
  //  - reviewedAt gesetzt -> der Kunde hat laengst entschieden, gescheitert
  //                         sind die Bestaetigungen danach. Erneut senden
  //                         wuerde ihm den Freigabe-Link nochmal schicken;
  //                         die Cloud Function lehnt es ohnehin ab.
  // emailSendError bleibt fuer immer am Dokument stehen — weder die
  // Kundenentscheidung noch ein spaeterer Erfolg raeumt es weg. Ein Klick auf
  // "Erledigt" setzt emailErrorDismissedAt; der Grund bleibt zur Nachschau
  // erhalten, die Warnung verschwindet.
  const emailErrorDrafts = drafts.filter(
      (d) => d.emailSendError && !d.emailErrorDismissedAt
          && getStatus(d) !== "archived");
  const emailErrorCount = emailErrorDrafts.length;
  const undeliveredCount = emailErrorDrafts.filter((d) => !d.reviewedAt).length;
  const afterDecisionCount = emailErrorCount - undeliveredCount;

  const filtered = drafts.filter((d) => {
    if (filter === "aktiv" && getStatus(d) === "archived") return false;
    if (filter !== "all" && filter !== "aktiv" && getStatus(d) !== filter) return false;
    if (searchTerm) {
      const matchesOrder = (d.orderNumber || "").toLowerCase().includes(searchTerm);
      const matchesEmail = (d.customerEmail || "").toLowerCase().includes(searchTerm);
      if (!matchesOrder && !matchesEmail) return false;
    }
    return true;
  });

  // Nur eigene Dateien loeschen. `storagePath` zeigt nicht immer auf einen
  // eigenen Upload: Die Freigabe eines Auto-Entwurfs macht keine Kopie,
  // sondern verweist auf das Original des Personalization Tools unter
  // `candidates/…_preview.jpg`, das dem Kandidaten weiter gehoert.
  // Am 14.09.2026 loeschte „⚠ Archivieren" genau so die Vorschau des
  // Kandidaten zu Bestellung 4174448180 — Karte mit kaputtem Bild, Datei
  // unwiederbringlich weg, waehrend Druckdatei und Referenzbilder blieben.
  // (Die Cloud Functions haben dieselbe Sperre; dort heisst sie genauso.)
  // Erlaubnisliste statt Sperre gegen `candidates/`: Eigene Uploads liegen
  // immer unter `drafts/`. Ein unbekanntes Praefix bleibt damit unangetastet.
  // A pending draft is stale when its order has moved out of "entwurf_raus"
  const isDraftStale = (draft) =>
    !draft.draftArchived &&
    draft.orderNumber &&
    draft.orderNumber in orderStages &&
    orderStages[draft.orderNumber] !== "entwurf_raus";

  // Archive a single stale draft: delete Storage files + mark archived
  const handleArchiveDraft = async (draft) => {
    try {
      if (isOwnDraftFile(draft.storagePath)) {
        try { await deleteObject(ref(storage, draft.storagePath)); } catch (_) {}
      }
      for (const img of draft.images || []) {
        if (isOwnDraftFile(img?.storagePath)) {
          try { await deleteObject(ref(storage, img.storagePath)); } catch (_) {}
        }
      }
      await updateDoc(doc(db, "drafts", draft.id), {
        reviewedAt:    serverTimestamp(),
        draftArchived: true,
      });
      setDrafts((prev) => prev.map((d) =>
        d.id === draft.id
          ? { ...d, reviewedAt: new Date(), draftArchived: true }
          : d
      ));
    } catch (err) {
      alert("Archivieren fehlgeschlagen: " + err.message);
    }
  };

  // ── Delete mode helpers ───────────────────────────────────────────────────

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectInRange = () => {
    if (!dateFrom && !dateTo) return;
    const from = dateFrom ? new Date(dateFrom) : null;
    const to   = dateTo   ? new Date(dateTo + "T23:59:59") : null;
    setSelected((prev) => {
      const next = new Set(prev);
      filtered.forEach((d) => {
        if (!d.createdAt) return;
        const created = d.createdAt.toDate();
        if (from && created < from) return;
        if (to   && created > to)   return;
        next.add(d.id);
      });
      return next;
    });
  };

  const exitDeleteMode = () => {
    setDeleteMode(false);
    setSelected(new Set());
    setDateFrom("");
    setDateTo("");
    setConfirmOpen(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await Promise.all(
        [...selected].map(async (id) => {
          const draft = drafts.find((d) => d.id === id);
          // Delete single-image file — nur eigene, siehe isOwnDraftFile
          if (isOwnDraftFile(draft?.storagePath)) {
            try { await deleteObject(ref(storage, draft.storagePath)); } catch (_) {}
          }
          // Delete multi-image files
          for (const img of draft?.images || []) {
            if (isOwnDraftFile(img?.storagePath)) {
              try { await deleteObject(ref(storage, img.storagePath)); } catch (_) {}
            }
          }
          await deleteDoc(doc(db, "drafts", id));
        })
      );
      setDrafts((prev) => prev.filter((d) => !selected.has(d.id)));
      exitDeleteMode();
    } catch (err) {
      alert("Löschen fehlgeschlagen: " + err.message);
    } finally {
      setDeleting(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────

  const toggleStyle = (active) => ({
    padding: "0.4em 1.1em",
    borderRadius: "6px",
    border: "1px solid #cbd5e0",
    background: active ? "#4a2c2a" : "#fff",
    color: active ? "#fff" : "#4a5568",
    fontWeight: "600",
    fontSize: "0.85rem",
    cursor: "pointer",
  });

  const counts = {
    aktiv:        drafts.filter((d) => getStatus(d) !== "archived").length,
    pending:      drafts.filter((d) => getStatus(d) === "pending").length,
    modification: drafts.filter((d) => getStatus(d) === "modification").length,
    approved:     drafts.filter((d) => getStatus(d) === "approved").length,
    archived:     drafts.filter((d) => getStatus(d) === "archived").length,
    all:          drafts.length,
  };

  const FILTERS = [
    { key: "aktiv",        label: "Aktiv" },
    { key: "pending",      label: "Offen" },
    { key: "modification", label: "Änderung" },
    { key: "approved",     label: "Freigegeben" },
    { key: "archived",     label: "Archiviert" },
    { key: "all",          label: "Alle" },
  ];

  const HEADERS = [
    ...(deleteMode ? [""] : []),
    "Created", "Order No.", "Customer", "Email", "Status",
    "1st Reminder", "2nd Reminder", "3rd Reminder", "Feedback", "Link",
  ];

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      {/* Header row: title + delete button */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "0.75em", gap: "1em", flexWrap: "wrap",
      }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#1a202c" }}>
          Entwurfsübersicht
        </h2>
        {!deleteMode ? (
          <button
            onClick={() => setDeleteMode(true)}
            style={{
              padding: "0.4em 1.1em", borderRadius: "6px",
              border: "1px solid #fc8181", background: "#fff",
              color: "#c53030", fontWeight: "600", fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            Entwürfe löschen
          </button>
        ) : (
          <button
            onClick={exitDeleteMode}
            style={{
              padding: "0.4em 1.1em", borderRadius: "6px",
              border: "1px solid #cbd5e0", background: "#fff",
              color: "#4a5568", fontWeight: "600", fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← Abbrechen
          </button>
        )}
      </div>

      {/* Filter pills (hidden in delete mode) */}
      {!deleteMode && (
        <div style={{ display: "flex", gap: "0.5em", flexWrap: "wrap", marginBottom: "0.85em" }}>
          {FILTERS.map(({ key, label }) => (
            <button
              key={key}
              style={toggleStyle(filter === key)}
              onClick={() => setFilter(key)}
            >
              {label} ({counts[key]})
            </button>
          ))}
        </div>
      )}

      {/* Delete mode banner + controls */}
      {deleteMode && (
        <div style={{
          background: "#fff5f5", border: "1px solid #fc8181",
          borderRadius: "8px", padding: "1em 1.25em",
          marginBottom: "1em",
        }}>
          <p style={{
            margin: "0 0 0.75em", color: "#c53030",
            fontWeight: "600", fontSize: "0.875rem",
          }}>
            Lösch-Modus aktiv — wähle Entwürfe aus, die du löschen möchtest.
          </p>

          {/* Date range selector */}
          <div style={{
            display: "flex", gap: "0.75em", alignItems: "center", flexWrap: "wrap",
          }}>
            <span style={{ fontSize: "0.8rem", color: "#744210", fontWeight: "600" }}>
              Zeitraum:
            </span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                padding: "0.35em 0.6em", border: "1px solid #cbd5e0",
                borderRadius: "5px", fontSize: "0.8rem", color: "#2d3748",
              }}
            />
            <span style={{ fontSize: "0.8rem", color: "#718096" }}>bis</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                padding: "0.35em 0.6em", border: "1px solid #cbd5e0",
                borderRadius: "5px", fontSize: "0.8rem", color: "#2d3748",
              }}
            />
            <button
              onClick={selectInRange}
              style={{
                padding: "0.35em 0.9em", borderRadius: "5px",
                border: "1px solid #cbd5e0", background: "#fff",
                color: "#4a5568", fontWeight: "600", fontSize: "0.8rem",
                cursor: "pointer",
              }}
            >
              In Zeitraum auswählen
            </button>
            {selected.size > 0 && (
              <button
                onClick={() => setSelected(new Set())}
                style={{
                  padding: "0.35em 0.9em", borderRadius: "5px",
                  border: "1px solid #e2e8f0", background: "none",
                  color: "#718096", fontSize: "0.8rem", cursor: "pointer",
                }}
              >
                Auswahl aufheben
              </button>
            )}
            {selected.size > 0 && (
              <button
                onClick={() => setConfirmOpen(true)}
                style={{
                  padding: "0.4em 1.1em", borderRadius: "5px",
                  border: "none", background: "#c53030",
                  color: "#fff", fontWeight: "700", fontSize: "0.85rem",
                  cursor: "pointer", marginLeft: "auto",
                }}
              >
                {selected.size} löschen
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search bar (hidden in delete mode — date range replaces it) */}
      {!deleteMode && (
        <div style={{ marginBottom: "1.25em" }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order number or customer email…"
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "0.55em 0.9em",
              border: "1px solid #cbd5e0", borderRadius: "6px",
              fontSize: "0.875rem", color: "#2d3748",
              background: "#fff", outline: "none",
            }}
          />
        </div>
      )}

      {/* E-Mail-Fehler: jeder betroffene Auftrag einzeln, mit Grund und
          nächstem Schritt (siehe describeEmailError). */}
      {emailErrorCount > 0 && !deleteMode && (
        <div style={{
          background: "#fff5f5", border: "1px solid #fc8181",
          borderRadius: "8px", padding: "0.75em 1.1em", marginBottom: "1em",
        }}>
          <div style={{ color: "#c53030", fontWeight: 700, fontSize: "0.9rem",
                        marginBottom: "0.4em" }}>
            ⚠ E-Mail-Probleme bei {emailErrorCount} Auftrag
            {emailErrorCount > 1 ? "en" : ""}
            {undeliveredCount > 0 && ` – davon ${undeliveredCount} blockierend`}
            {afterDecisionCount > 0 && undeliveredCount > 0 &&
              `, ${afterDecisionCount} nur zur Info`}
          </div>
          {emailErrorDrafts.map((d) => {
            const e = describeEmailError(d);
            return (
              <div key={d.id} style={{
                borderTop: "1px solid #fed7d7", padding: "0.5em 0",
                fontSize: "0.84rem", color: "#742a2a", lineHeight: 1.45,
              }}>
                <div>
                  <strong>{e.order}</strong> · {e.who} ·{" "}
                  <span style={{
                    fontWeight: 700,
                    color: e.severity === "blocking" ? "#c53030" : "#975a16",
                  }}>{e.headline}</span>
                  {d.emailSendErrorAt && (
                    <span style={{ color: "#a0aec0" }}> · {formatDate(d.emailSendErrorAt)}</span>
                  )}
                </div>
                <div>{e.effect} <em>{e.action}</em></div>
                <div style={{ color: "#9b2c2c", fontSize: "0.78rem" }}>{e.reason}</div>
                <div style={{ marginTop: "0.3em", display: "flex", gap: "0.5em" }}>
                  {d.orderNumber && (
                    <button type="button" onClick={() => setSearch(d.orderNumber)}
                      style={{ fontSize: "0.75rem", padding: "0.15em 0.6em",
                               borderRadius: 6, border: "1px solid #fc8181",
                               background: "#fff", color: "#c53030", cursor: "pointer" }}>
                      Zur Zeile
                    </button>
                  )}
                  <button type="button" onClick={() => handleDismissEmailError(d)}
                    disabled={!!dismissingError[d.id]}
                    style={{ fontSize: "0.75rem", padding: "0.15em 0.6em",
                             borderRadius: 6, border: "1px solid #cbd5e0",
                             background: "#fff", color: "#4a5568", cursor: "pointer" }}>
                    {dismissingError[d.id] ? "…" : "✓ Erledigt"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading ? (
        <p style={{ color: "#718096", fontStyle: "italic" }}>Loading drafts…</p>
      ) : filtered.length === 0 ? (
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          padding: "3em", textAlign: "center", color: "#718096",
        }}>
          <p style={{ fontSize: "1.05rem", margin: 0 }}>
            {searchTerm
              ? `No drafts match "${search}".`
              : filter === "pending"
                ? "No pending drafts."
                : filter === "modification"
                  ? "No drafts awaiting modification."
                  : filter === "approved"
                    ? "No approved drafts yet."
                    : "No drafts found."}
          </p>
        </div>
      ) : (
        <div style={{
          background: "#ffffff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          overflowX: "auto",
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ background: "#fdf7f4", borderBottom: "2px solid #e2e8f0" }}>
                {HEADERS.map((h, i) => (
                  <th key={i} style={{
                    padding: "0.75em 1em", textAlign: "left",
                    fontWeight: "700", color: "#4a5568", fontSize: "0.75rem",
                    letterSpacing: "0.05em", textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((draft, i) => {
                const status   = getStatus(draft);
                const isSelected = selected.has(draft.id);
                const rowBg = deleteMode
                  ? (isSelected ? "#fff5f5" : (i % 2 === 0 ? "#fff" : "#fafafa"))
                  : (i % 2 === 0 ? "#fff" : "#fafafa");

                return (
                  <tr
                    key={draft.id}
                    onClick={deleteMode ? () => toggleSelect(draft.id) : undefined}
                    style={{
                      borderBottom: "1px solid #edf2f7",
                      background: rowBg,
                      cursor: deleteMode ? "pointer" : "default",
                      outline: isSelected ? "2px solid #fc8181" : "none",
                      outlineOffset: "-1px",
                    }}
                  >
                    {/* Checkbox column (delete mode only) */}
                    {deleteMode && (
                      <td style={{ padding: "0.85em 0.75em 0.85em 1em", width: "2em" }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(draft.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: "1em", height: "1em", cursor: "pointer" }}
                        />
                      </td>
                    )}
                    {/* Created */}
                    <td style={{ padding: "0.85em 1em", color: "#718096", whiteSpace: "nowrap" }}>
                      {formatDate(draft.createdAt)}
                    </td>
                    {/* Order No. */}
                    <td style={{ padding: "0.85em 1em", color: "#2d3748", fontWeight: "600" }}>
                      {draft.orderNumber || <span style={{ color: "#a0aec0" }}>—</span>}
                    </td>
                    {/* Customer name */}
                    <td style={{ padding: "0.85em 1em", color: "#2d3748" }}>
                      {draft.customerFirstName || <span style={{ color: "#a0aec0" }}>—</span>}
                    </td>
                    {/* Email */}
                    <td style={{ padding: "0.85em 1em", color: "#4a5568" }}>
                      {draft.customerEmail || <span style={{ color: "#a0aec0" }}>—</span>}
                      {isDomainProblem(draft.customerEmail) && getStatus(draft) === "pending" && (() => {
                        const qentry = etsyQueueMap[draft.id];
                        if (draft.etsyDraftWelcomeSentAt) {
                          return (
                            <span style={{
                              display: "inline-block", marginLeft: "6px",
                              padding: "0.15em 0.5em", borderRadius: "10px",
                              background: "#ebf8ff", color: "#2b6cb0",
                              border: "1px solid #90cdf4",
                              fontSize: "0.72rem", fontWeight: "600",
                            }}>Etsy ✓</span>
                          );
                        }
                        if (qentry?.status === "error") {
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", marginLeft: "6px" }}>
                              <span
                                title={`Etsy-Nachricht zum Entwurf für #${draft.orderNumber || "?"}${draft.customerFirstName ? ` (${draft.customerFirstName})` : ""} nicht gesendet.\n${qentry.errorMsg ? `Grund: ${qentry.errorMsg}` : "Ein Grund wurde nicht aufgezeichnet."}\nText zum Kopieren und „Erledigt“ im Dashboard.`}
                                style={{
                                  padding: "0.15em 0.5em", borderRadius: "10px",
                                  background: "#fff5f5", color: "#c53030",
                                  border: "1px solid #fc8181",
                                  fontSize: "0.72rem", fontWeight: "600", cursor: "help",
                                }}>Etsy ✗</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRetryDraftEtsy(draft); }}
                                disabled={!!retryingEtsy[draft.id]}
                                style={{
                                  padding: "0.15em 0.5em", borderRadius: "10px",
                                  background: "#ebf8ff", color: "#2b6cb0",
                                  border: "1px solid #90cdf4",
                                  fontSize: "0.72rem", fontWeight: "600",
                                  cursor: retryingEtsy[draft.id] ? "default" : "pointer",
                                  opacity: retryingEtsy[draft.id] ? 0.6 : 1,
                                }}>
                                {retryingEtsy[draft.id] ? "…" : "↺ Erneut"}
                              </button>
                            </span>
                          );
                        }
                        if (qentry?.status === "pending") {
                          return (
                            <span style={{
                              display: "inline-block", marginLeft: "6px",
                              padding: "0.15em 0.5em", borderRadius: "10px",
                              background: "#fefcbf", color: "#744210",
                              border: "1px solid #f6e05e",
                              fontSize: "0.72rem", fontWeight: "600",
                            }}>Etsy ausstehend</span>
                          );
                        }
                        return (
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePostDraftToEtsy(draft); }}
                            title="Entwurfs-Link direkt über Etsy-Nachrichten senden"
                            style={{
                              display: "inline-block", marginLeft: "6px",
                              padding: "0.15em 0.5em", borderRadius: "10px",
                              background: "#ebf8ff", color: "#2b6cb0",
                              border: "1px solid #90cdf4",
                              fontSize: "0.72rem", fontWeight: "600", cursor: "pointer",
                            }}>
                            Auf Etsy posten
                          </button>
                        );
                      })()}
                    </td>
                    {/* Status */}
                    <td style={{ padding: "0.85em 1em" }}>
                      <StatusBadge
                        status={status}
                        onClick={!deleteMode && status === "modification"
                          ? () => setModal(draft)
                          : undefined}
                      />
                      {draft.emailSendError && !draft.emailErrorDismissedAt && (
                        <>
                          <span
                            title={(() => {
                              const e = describeEmailError(draft);
                              return `${e.order} · ${e.who}\n${e.headline}\n\n`
                                + `${e.effect}\n${e.action}\n\n${e.reason}`;
                            })()}
                            style={{
                              marginLeft: "6px",
                              padding: "0.15em 0.5em",
                              borderRadius: "10px",
                              border: "1px solid #fc8181",
                              background: "#fff5f5",
                              color: "#c53030",
                              fontSize: "0.72rem",
                              fontWeight: "600",
                              whiteSpace: "nowrap",
                              cursor: "help",
                            }}
                          >
                            {draft.reviewedAt ? "⚠ Bestätigung" : "⚠ E-Mail"}
                          </span>
                          {!draft.reviewedAt && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRetryInitial(draft);
                            }}
                            disabled={!!retryingInitial[draft.id]}
                            style={{
                              marginLeft: "4px",
                              padding: "0.15em 0.5em",
                              borderRadius: "10px",
                              border: "1px solid #90cdf4",
                              background: "#ebf8ff",
                              color: "#2b6cb0",
                              fontSize: "0.72rem",
                              fontWeight: "600",
                              whiteSpace: "nowrap",
                              cursor: retryingInitial[draft.id]
                                ? "default" : "pointer",
                              opacity: retryingInitial[draft.id] ? 0.6 : 1,
                            }}
                          >
                            {retryingInitial[draft.id] ? "…" : "↺ Erneut"}
                          </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDismissEmailError(draft);
                            }}
                            disabled={!!dismissingError[draft.id]}
                            title={"Hinweis ausblenden — der Grund bleibt am "
                              + "Entwurf gespeichert"}
                            style={{
                              marginLeft: "4px",
                              padding: "0.15em 0.5em",
                              borderRadius: "10px",
                              border: "1px solid #cbd5e0",
                              background: "#f7fafc",
                              color: "#4a5568",
                              fontSize: "0.72rem",
                              fontWeight: "600",
                              whiteSpace: "nowrap",
                              cursor: dismissingError[draft.id]
                                ? "default" : "pointer",
                              opacity: dismissingError[draft.id] ? 0.6 : 1,
                            }}
                          >
                            {dismissingError[draft.id] ? "…" : "✓ Erledigt"}
                          </button>
                        </>
                      )}
                      {!deleteMode && isDraftStale(draft) && (
                        <button
                          onClick={() => handleArchiveDraft(draft)}
                          title="Bestellung ist nicht mehr in 'Entwurf raus' — Entwurf archivieren"
                          style={{
                            marginLeft: "6px",
                            padding: "0.15em 0.5em",
                            borderRadius: "10px",
                            border: "1px solid #fc8181",
                            background: "#fff5f5",
                            color: "#c53030",
                            fontSize: "0.72rem",
                            fontWeight: "600",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          ⚠ Archivieren
                        </button>
                      )}
                    </td>
                    {/* 1st Reminder */}
                    <td style={{ padding: "0.85em 1em", color: "#718096",
                      fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                      {formatDate(draft.reminder1SentAt)}
                      {draft.reminderError && !draft.reminder1SentAt && (
                        <DraftErrorBadge draft={draft} retrying={retrying} onRetry={handleRetry} />
                      )}
                    </td>
                    {/* 2nd Reminder */}
                    <td style={{ padding: "0.85em 1em", color: "#718096",
                      fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                      {formatDate(draft.reminder2SentAt)}
                      {(draft.etsyMessage2SentAt ||
                        (!draft.etsyMessage2SentAt && !draft.etsyMessage3SentAt &&
                         draft.etsyMessageSentAt && !draft.reminder3SentAt)) && (
                        <span style={{
                          marginLeft: "0.4em",
                          padding: "0.1em 0.5em",
                          borderRadius: "10px",
                          border: "1px solid #b794f4",
                          background: "#e9d8fd",
                          color: "#553c9a",
                          fontSize: "0.72rem",
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}>
                          Etsy Nachricht
                        </span>
                      )}
                      {draft.reminderError && draft.reminder1SentAt && !draft.reminder2SentAt && (
                        <DraftErrorBadge draft={draft} retrying={retrying} onRetry={handleRetry} />
                      )}
                    </td>
                    {/* 3rd Reminder */}
                    <td style={{ padding: "0.85em 1em", color: "#718096",
                      fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                      {formatDate(draft.reminder3SentAt)}
                      {(draft.etsyMessage3SentAt ||
                        (!draft.etsyMessage2SentAt && !draft.etsyMessage3SentAt &&
                         draft.etsyMessageSentAt && draft.reminder3SentAt)) && (
                        <span style={{
                          marginLeft: "0.4em",
                          padding: "0.1em 0.5em",
                          borderRadius: "10px",
                          border: "1px solid #b794f4",
                          background: "#e9d8fd",
                          color: "#553c9a",
                          fontSize: "0.72rem",
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}>
                          Etsy Nachricht
                        </span>
                      )}
                      {draft.reminderError && draft.reminder2SentAt && !draft.reminder3SentAt && (
                        <DraftErrorBadge draft={draft} retrying={retrying} onRetry={handleRetry} />
                      )}
                    </td>
                    {/* Feedback received */}
                    <td style={{ padding: "0.85em 1em", color: "#718096",
                      fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                      {formatDate(draft.reviewedAt)}
                    </td>
                    {/* Link */}
                    <td style={{ padding: "0.85em 1em" }}>
                      <a
                        href={`/review/${draft.id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => deleteMode && e.preventDefault()}
                        style={{
                          padding: "0.3em 0.75em", borderRadius: "5px",
                          border: "1px solid #cbd5e0", background: "#fff",
                          color: "#4a2c2a", fontSize: "0.8rem", fontWeight: "500",
                          textDecoration: "none", whiteSpace: "nowrap",
                          display: "inline-block",
                          opacity: deleteMode ? 0.4 : 1,
                        }}
                      >
                        Link ↗
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ marginTop: "1em", color: "#a0aec0", fontSize: "0.8rem", textAlign: "right" }}>
        {deleteMode && selected.size > 0
          ? `${selected.size} ausgewählt · `
          : ""}
        Showing {filtered.length} of {drafts.length} draft{drafts.length !== 1 ? "s" : ""}
      </p>

      {/* Modification detail modal */}
      {modal && <ModificationModal draft={modal} onClose={() => setModal(null)} />}

      {/* Delete confirmation modal */}
      {confirmOpen && (
        <ConfirmDeleteModal
          count={selected.size}
          onConfirm={handleDelete}
          onCancel={() => setConfirmOpen(false)}
          deleting={deleting}
        />
      )}
    </div>
  );
};

export default DraftOverview;
